import React, { useLayoutEffect, useRef, useState } from 'react'
import { throttle } from 'lodash'

import DocPreview from 'components/DocPreview'
import type { ArtboardState, IdType, MarkupState } from 'EditorApp/types'

declare global {
  // Set by app/views/shared/_config.html.erb, so it is absent in unit tests.
  var CONFIG_VIEW_RESIZE_DELAY: number | undefined
}

type DocShowProps = {
  fontFamily: string
  markup: MarkupState
}

// DocPreview only renders the first page of the markup it is given, so each
// page gets its own single-page slice. Slicing (rather than picking keys)
// keeps everything else — aiLabel, animation — intact.
const singlePageMarkup = (
  artboard: ArtboardState,
  pageId: IdType
): ArtboardState => ({
  ...artboard,
  pageOrder: [pageId],
  pages: { [pageId]: artboard.pages[pageId] },
})

type ArtboardPagesProps = {
  artboard: ArtboardState
  fontFamily: string
  showName: boolean
  width: number
}

const ArtboardPages = ({
  artboard,
  fontFamily,
  showName,
  width,
}: ArtboardPagesProps) => {
  const scale = width ? width / artboard.pageWidth : 1

  return (
    <section className="DocShow__artboard">
      {showName && <h2 className="DocShow__artboardName">{artboard.name}</h2>}

      {artboard.pageOrder.map((pageId) => (
        <div
          className="DocShow__page"
          key={pageId}
          style={{ height: artboard.pageHeight * scale }}
        >
          <div
            className="DocShow__page-content"
            style={{ transform: `scale(${scale})` }}
          >
            <DocPreview
              fontFamily={fontFamily}
              markup={singlePageMarkup(artboard, pageId)}
              zoom={scale}
              previewImage={false}
            />
          </div>
        </div>
      ))}
    </section>
  )
}

const DocShow = ({ fontFamily, markup }: DocShowProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)

  const artboards = markup.artboardOrder.map(
    (artboardId) => markup.artboards[artboardId]
  )

  useLayoutEffect(() => {
    const updateWidth = throttle(() => {
      if (!containerRef.current) {
        return
      }

      setWidth(containerRef.current.getBoundingClientRect().width)
    }, globalThis.CONFIG_VIEW_RESIZE_DELAY)

    updateWidth()

    window.addEventListener('resize', updateWidth)

    return () => {
      window.removeEventListener('resize', updateWidth)
      updateWidth.cancel()
    }
  }, [])

  return (
    <div className="DocShow" ref={containerRef}>
      {artboards.map((artboard) => (
        <ArtboardPages
          key={artboard.id}
          artboard={artboard}
          fontFamily={fontFamily}
          showName={artboards.length > 1}
          width={width}
        />
      ))}
    </div>
  )
}

export default DocShow
